import { Injectable } from '@angular/core';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { es } from 'date-fns/locale'; // Import Spanish locale

@Injectable({
	providedIn: 'root', // Service available globally
})
export class DateService {
	/**
	 * Converts an ISO date (e.g., 2025-02-14T18:13:51.758Z) to DD/MM/YYYY format.
	 * @param isoDate The date in ISO format or already formatted as DD/MM/YYYY.
	 * @returns The date formatted as 'DD/MM/YYYY' or an empty string if invalid.
	 */
	formatToDDMMYYYY(isoDate: string): string {
		if (!isoDate) return '';

		// If already formatted as DD/MM/YYYY, return as is
		const datePattern = /^\d{2}\/\d{2}\/\d{4}$/;
		if (datePattern.test(isoDate)) {
			return isoDate;
		}

		try {
			const date = parseISO(isoDate); // Convert ISO string to Date object
			return format(date, 'dd/MM/yyyy'); // Apply format
		} catch (error) {
			console.error('❌ Error formatting the date:', isoDate, error);
			return '';
		}
	}

	/**
	 * Extracts the time from an ISO date and returns it in HH:mm format.
	 */
	formatToHHMM(isoDate: string): string {
		if (!isoDate) return '';
		try {
			const date = parseISO(isoDate);
			return format(date, 'HH:mm'); // Extract only the time
		} catch (error) {
			console.error('❌ Error formatting the time:', isoDate, error);
			return '';
		}
	}

	/**
	 * Calculates time elapsed from a date until now
	 * Example: "hace 5 minutos", "hace 2 horas", "hace 3 días"
	 */
	timeAgo(isoDate: string): string {
		if (!isoDate) return '';
		try {
			const date = parseISO(isoDate);
			return formatDistanceToNow(date, {
				addSuffix: true,
				locale: es,
			});
		} catch (error) {
			console.error('❌ Error calculating time ago:', isoDate, error);
			return '';
		}
	}

	/**
	 * Formats date as "D MMMM" in Spanish
	 * Example: "3 septiembre"
	 */
	formatToDayMonth(date: Date | string): string {
		if (!date) return '';
		try {
			const dateObj = typeof date === 'string' ? parseISO(date) : date;
			return format(dateObj, 'd MMMM', { locale: es }).toLowerCase();
		} catch (error) {
			console.error('❌ Error formatting date to day month:', date, error);
			return '';
		}
	}

	/**
	 * Formats date and time as dd/mm/aa - hh:mm
	 * @param isoDate Date in ISO format
	 * @returns Formatted date like '14/10/25 - 15:30' or empty if invalid
	 */
	formatToDDMMAAHHMM(isoDate: string): string {
		if (!isoDate) return '';
		try {
			const date = parseISO(isoDate);
			return format(date, 'dd/MM/yy - HH:mm');
		} catch (error) {
			console.error('❌ Error formatting date and time:', isoDate, error);
			return '';
		}
	}

	timeAgoShort(isoDate: string): string {
		if (!isoDate) return '';
		try {
			const date = parseISO(isoDate);
			const timeAgo = formatDistanceToNow(date, { locale: es });

			// Simplify but keep "hace"
			return (
				'Hace ' +
				timeAgo
					.replace('alrededor de ', '')
					.replace('menos de ', '')
					.replace('casi ', '')
					.replace('más de ', '')
			);
		} catch (error) {
			console.error('❌ Error calculating time ago:', isoDate, error);
			return '';
		}
	}
}
